/**
 * AlshallalLogo — Reusable, animated brand mark.
 *
 * The Alshallal logo is intrinsically orange + white on transparent. To match
 * varying surface colors (beige page bg, black sidebar header, etc.) we wrap
 * it in a soft glow container with a tasteful entrance animation.
 */
import React from 'react';
import { motion } from 'framer-motion';
import alshallalLogo from '@/assets/brand/alshallal-logo.png';
import { cn } from '@/lib/utils';

export const AlshallalLogo = ({
  size = 'md',
  showWordmark = false,
  wordmarkAr = false,
  className,
  animated = true,
  variant = 'plain',         // 'plain' | 'on-dark' | 'on-light'
}) => {
  const sizes = {
    xs: 'h-7 w-7',
    sm: 'h-9 w-9',
    md: 'h-12 w-12',
    lg: 'h-20 w-20',
    xl: 'h-28 w-28',
  };

  const Wrapper = animated ? motion.div : 'div';
  const motionProps = animated
    ? {
        initial: { opacity: 0, scale: 0.85, rotate: -6 },
        animate: { opacity: 1, scale: 1, rotate: 0 },
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
      }
    : {};

  return (
    <div className={cn('flex items-center gap-3', className)} data-testid="alshallal-logo">
      <Wrapper
        {...motionProps}
        className={cn(
          'relative flex items-center justify-center will-change-transform',
          'transition-transform duration-300',
          sizes[size] || sizes.md
        )}
      >
        <img
          src={alshallalLogo}
          alt="Alshallal"
          className="h-full w-full object-contain"
          draggable={false}
        />
      </Wrapper>

      {showWordmark && (
        <div className="flex flex-col leading-tight">
          <span className={cn(
            'font-bold tracking-tight',
            size === 'xl' || size === 'lg' ? 'text-2xl' : 'text-lg',
            variant === 'on-dark' ? 'text-brand-beige' : 'text-brand-black'
          )}>
            {wordmarkAr ? 'معجنات الشلال' : 'Alshallal Pastry'}
          </span>
          <span className={cn(
            'text-xs font-medium',
            variant === 'on-dark' ? 'text-brand-orange-60' : 'text-brand-brown'
          )}>
            {wordmarkAr ? 'للموارد البشرية' : 'Human Resources'}
          </span>
        </div>
      )}
    </div>
  );
};

export default AlshallalLogo;
